/**
 * PROTECTED-DEVICES-MANAGE-1E — fonte ÚNICA de estado de proteção no frontend.
 *
 * Normaliza as formas que o backend expõe, em ordem de riqueza:
 *   1) center.protection (DeviceDetail) — effective_protected + source + immutable;
 *   2) DTO de lista/mapa (1E) — effective_protected + protection_source;
 *   3) legado ota_protected (ENV-only) — fallback.
 *
 * O backend é a verdade (a trava real é o enqueue_command guard); aqui só decidimos
 * o SINAL visual. Estado desconhecido NUNCA vira falso "não protegido" → known=false
 * (UI deve mostrar neutro). Não confiar em lista hardcoded p/ segurança.
 */
const IMMUTABLE_SOURCES = new Set(["env", "production", "reference"]);

const SOURCE_LABEL = {
  production: "Produção",
  reference: "Referência",
  env: "Ambiente (.env)",
  manual: "Manual",
};

export function getProtectionState(device) {
  if (!device || typeof device !== "object") {
    return { known: false, protected: false, source: null, immutable: false };
  }
  // 1) center.protection — o mais rico (DeviceDetail / device-center).
  const p = device.protection;
  if (p && typeof p.effective_protected === "boolean") {
    const source = p.source ?? null;
    const immutable = typeof p.immutable === "boolean"
      ? p.immutable
      : (source ? IMMUTABLE_SOURCES.has(source) : false);
    return { known: true, protected: p.effective_protected, source, immutable };
  }
  // 2) DTO de lista/mapa (1E) — effective_protected + protection_source.
  if (typeof device.effective_protected === "boolean") {
    const source = device.protection_source ?? null;
    return {
      known: true,
      protected: device.effective_protected,
      source,
      immutable: source ? IMMUTABLE_SOURCES.has(source) : false,
    };
  }
  // 3) Legado: ota_protected = ENV-only (fallback enquanto o DTO não traz o efetivo).
  if (typeof device.ota_protected === "boolean") {
    return {
      known: true,
      protected: device.ota_protected,
      source: device.ota_protected ? "env" : null,
      immutable: device.ota_protected,
    };
  }
  // 4) Sem nenhum sinal → desconhecido (neutro, nunca falso "não protegido").
  return { known: false, protected: false, source: null, immutable: false };
}

export function protectionSourceLabel(source) {
  return source ? SOURCE_LABEL[source] || source : null;
}

/** true se o device é de REFERÊNCIA (gold/canário/piloto) pela verdade do backend. */
export function isReferenceProtected(device) {
  return getProtectionState(device).source === "reference";
}
